<script>
import "leaflet/dist/leaflet.css";
import * as L from "leaflet";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";

// Fix default marker icon paths for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL("leaflet/dist/images/marker-icon-2x.png", import.meta.url).href,
  iconUrl: new URL("leaflet/dist/images/marker-icon.png", import.meta.url).href,
  shadowUrl: new URL("leaflet/dist/images/marker-shadow.png", import.meta.url).href,
});

export default {
  components: { LMap, LTileLayer, LMarker },
  props: {
    street: { type: String, default: "" },
    city: { type: String, default: "" },
    state: { type: String, default: "" },
    postalCode: { type: String, default: "" },
    country: { type: String, default: "" }
  },
  data() {
    return {
      coords: null,
      loading: true,
      error: false
    };
  },
  computed: {
    hasAddress() {
      return this.street || this.city || this.state || this.postalCode;
    }
  },
  watch: {
    street() { this.geocode(); },
    city() { this.geocode(); },
    state() { this.geocode(); },
    postalCode() { this.geocode(); },
    country() { this.geocode(); }
  },
  mounted() {
    this.geocode();
  },
  methods: {
    async geocode() {
      if (!this.hasAddress) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = false;

      // Try structured search first
      const params = new URLSearchParams({ format: "json", limit: "1" });
      if (this.street) params.set("street", this.street);
      if (this.city) params.set("city", this.city);
      if (this.state) params.set("state", this.state);
      if (this.postalCode) params.set("postalcode", this.postalCode);
      if (this.country) params.set("country", this.country);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
        const data = await res.json();
        if (data.length) {
          this.coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          this.loading = false;
          return;
        }

        // Fallback: free text search with full address
        const query = [this.street, this.city, this.state, this.postalCode, this.country].filter(Boolean).join(', ');
        const res2 = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`);
        const data2 = await res2.json();
        if (data2.length) {
          this.coords = [parseFloat(data2[0].lat), parseFloat(data2[0].lon)];
        } else {
          this.error = true;
        }
      } catch {
        this.error = true;
      }
      this.loading = false;
    }
  }
};
</script>

<template>
  <div class="household-map">
    <div
      v-if="!hasAddress"
      class="placeholder"
    >
      No address set
    </div>
    <div
      v-else-if="loading"
      class="placeholder"
    >
      Loading map...
    </div>
    <div
      v-else-if="error"
      class="placeholder"
    >
      Address not found in map data
    </div>
    <l-map
      v-else
      :zoom="15"
      :center="coords"
      :use-global-leaflet="true"
      class="map"
    >
      <l-tile-layer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; <a href='https://osm.org/copyright'>OpenStreetMap</a> contributors"
      />
      <l-marker :lat-lng="coords" />
    </l-map>
  </div>
</template>

<style scoped lang="scss">
.household-map {
  width: 100%;
  height: 300px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  position: relative;
  z-index: 0;
}
.map {
  width: 100%;
  height: 100%;
}
.placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
